import React, { FC, ReactNode } from "react";
import { Box } from "@chakra-ui/react";
import { LeadTypeTag } from "components";
import { ExtendedColumnInstance } from "components/table/table-props";
import {
  GRS_PENDING_ROW_BG,
  GRS_PENDING_ROW_HOVER_BG,
} from "constants/grs-pending-leads";
import { LeadTypeEnum } from "enums/lead-type.enum";
import { LeadsTableColumns } from "enums/leads-table-columns.enum";
import { TGrsPendingLead } from "types/grs-pending-lead.type";
import { TLead } from "types/lead.type";
import {
  formatGrsAddressLabel,
  formatGrsPendingLeadName,
} from "utils/grs-pending-leads.utils";
import {
  GrsPendingLeadDateCell,
  GrsPendingLeadPersonCell,
  GrsPendingLeadStatusCell,
} from "./GrsPendingLeadCells";

interface IGrsPendingLeadRowProps {
  pendingLead: TGrsPendingLead;
  columns: ExtendedColumnInstance<TLead>[];
  sourceTag: ReactNode;
  onAction: (pendingLead: TGrsPendingLead) => void;
}

export const GrsPendingLeadRow: FC<IGrsPendingLeadRowProps> = ({
  pendingLead,
  columns,
  sourceTag,
  onAction,
}) => {
  const leadName = formatGrsPendingLeadName(pendingLead);

  const renderCellContent = (
    column: ExtendedColumnInstance<TLead>
  ): ReactNode => {
    switch (column.key) {
      case LeadsTableColumns.DATE:
        return <GrsPendingLeadDateCell createdAt={pendingLead.createdAt} />;
      case LeadsTableColumns.LEAD_STATUS:
        return (
          <GrsPendingLeadStatusCell
            leadName={leadName}
            onAction={() => onAction(pendingLead)}
          />
        );
      case LeadsTableColumns.PERSON:
        return <GrsPendingLeadPersonCell leadName={leadName} />;
      case LeadsTableColumns.LEAD_SOURCE:
        return sourceTag;
      case LeadsTableColumns.LEAD_TYPE:
        return <LeadTypeTag type={LeadTypeEnum.Vendor} />;
      case LeadsTableColumns.SUBJECT:
        return formatGrsAddressLabel(pendingLead);
      default:
        return null;
    }
  };

  return (
    <Box
      display={"flex"}
      flex={"1 0 auto"}
      width={"max-content"}
      minWidth={"100%"}
      borderBottom={"1px solid"}
      borderColor={"gray.100"}
      bg={GRS_PENDING_ROW_BG}
      role={"row"}
      aria-label={`GRS lead ${leadName}, to be accepted`}
      _hover={{ bg: GRS_PENDING_ROW_HOVER_BG }}
    >
      {columns.map(column => (
        <Box
          key={column.id}
          role={"cell"}
          style={column.getHeaderProps().style}
          display={"flex"}
          alignItems={"center"}
          px={2}
          py={1}
          width={column.width}
          minWidth={column.minWidth}
          maxWidth={column.maxWidth}
        >
          {renderCellContent(column)}
        </Box>
      ))}
    </Box>
  );
};
